import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Organization } from "@shared/schema";
import {
  Building2,
  Plus,
  Settings,
  Palette,
  Plug,
  Package,
  ChevronRight,
  Check,
  X,
  Phone,
  Mail,
  MessageSquare,
  ExternalLink,
  TestTube,
  Trash2,
  Save,
  Upload,
  Eye,
  EyeOff,
  Lock,
  LogOut,
  Shield,
  Users,
  ShoppingCart,
  TrendingUp,
  Activity,
  Link2,
  Wrench,
  RefreshCw,
  Archive,
  RotateCcw,
  ClipboardList
} from "lucide-react";
import { useLocation } from "wouter";
import { StockImageMatchDialog } from "@/components/stock-image-match-dialog";
import { SuperAdminClydeSettings } from "@/components/super-admin-clyde-settings";
import { CowboyHatIcon } from "@/components/cowboy-hat-icon";
import { SiQuickbooks } from "react-icons/si";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Image } from "lucide-react";

// Super admin auth is cookie-driven (httpOnly super_admin_session). The client
// never sees the raw token. All super-admin API calls send credentials so the
// cookie travels with the request.

async function superAdminLogin(password: string): Promise<void> {
  const response = await fetch('/api/super-admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password })
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Login failed');
  }
}

async function superAdminLogout(): Promise<void> {
  try {
    await fetch('/api/super-admin/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — cookie clearing on the server side is the source of truth
  }
}

async function superAdminVerifySession(): Promise<boolean> {
  try {
    const response = await fetch('/api/super-admin/verify-session', {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => ({}));
    return data.valid === true;
  } catch {
    return false;
  }
}

async function superAdminRequest(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
      'Content-Type': 'application/json'
    }
  });

  if (response.status === 401) {
    throw new Error("Session expired - please log in again");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed: ${response.status}`);
  }

  return response.json();
}

const organizationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().min(1, "URL slug is required").regex(/^[a-z0-9-]+$/, "Only lowercase letters, numbers, and hyphens"),
  description: z.string().optional(),
  logoUrl: z.string().optional(),
  faviconUrl: z.string().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be valid hex color").optional(),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be valid hex color").optional(),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be valid hex color").optional(),
  companyEmail: z.string().email().optional().or(z.literal("")),
  companyPhone: z.string().optional(),
  companyAddress: z.string().optional(),
  twilioPhoneNumber: z.string().optional(),
  isActive: z.boolean().default(true),
  // S10 per-org opt-out from the native app's pre-auth vendor search.
  discoverable: z.boolean().default(true),
  // Paid add-on, off for every vendor until switched on here. The server audits the change.
  kioskEnabled: z.boolean().default(false)
});

type OrganizationForm = z.infer<typeof organizationSchema>;

interface IntegrationStatus {
  twilio: { configured: boolean; hasAccountSid?: boolean; hasPhoneNumber?: boolean };
  gmail: { configured: boolean; hasRefreshToken?: boolean; fromEmail?: string };
  quickbooks: { configured: boolean; hasAccessToken?: boolean; realmId?: string };
}

interface DashboardStats {
  organizations: { total: number; active: number; inactive: number };
  integrations: { twilio: number; gmail: number; quickbooks: number };
  totals: { orders: number; products: number; accounts: number };
  orgDetails: Array<{
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
    orders: number;
    products: number;
    accounts: number;
    integrations: { twilio: boolean; gmail: boolean; quickbooks: boolean };
  }>;
}

export default function SuperAdmin() {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [testingRefreshOrgId, setTestingRefreshOrgId] = useState<number | null>(null);
  const [testRefreshResult, setTestRefreshResult] = useState<{
    orgId: number;
    success: boolean;
    message: string;
    logs: string[];
  } | null>(null);

  useEffect(() => {
    superAdminVerifySession().then(valid => {
      setIsAuthenticated(valid);
    });
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      await superAdminLogin(loginPassword);
      setIsAuthenticated(true);
      setLoginPassword("");
    } catch (err: any) {
      setLoginError(err.message || "Invalid password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const [, setLocation] = useLocation();

  const handleLogout = async () => {
    await superAdminLogout();
    setIsAuthenticated(false);
    setSelectedOrg(null);
  };

  const { data: organizations = [], isLoading } = useQuery<Organization[]>({
    queryKey: ['/api/super-admin/organizations'],
    queryFn: () => superAdminRequest('/api/super-admin/organizations'),
    enabled: isAuthenticated === true
  });

  const { data: integrationStatus, refetch: refetchIntegrationStatus } = useQuery<IntegrationStatus>({
    queryKey: ['/api/super-admin/organizations', selectedOrg?.id, 'integrations'],
    queryFn: () => superAdminRequest(`/api/super-admin/organizations/${selectedOrg?.id}/integrations`),
    enabled: !!selectedOrg && isAuthenticated === true,
    refetchOnMount: "always", // Always refetch when component mounts or org changes
    refetchOnWindowFocus: "always" // Force refetch when window gains focus (after OAuth popup closes)
  });

  const { data: dashboardStats } = useQuery<DashboardStats>({
    queryKey: ['/api/super-admin/stats'],
    queryFn: () => superAdminRequest('/api/super-admin/stats'),
    enabled: isAuthenticated === true
  });

  // QuickBooks diagnostics query
  const { data: qbDiagnostics, refetch: refetchDiagnostics, isFetching: isFetchingDiagnostics } = useQuery<{
    timestamp: string;
    totalOrgs: number;
    connectedOrgs: number;
    diagnostics: Array<{
      orgId: number;
      orgName: string;
      slug: string;
      quickbooks: {
        hasRecord: boolean;
        isAuthenticated: boolean;
        companyId: string | null;
        hasAccessToken: boolean;
        hasRefreshToken: boolean;
        expiresAt: string | null;
        lastSync: string | null;
        createdAt: string | null;
        updatedAt: string | null;
      };
    }>;
  }>({
    queryKey: ['/api/super-admin/diagnostics/quickbooks'],
    queryFn: () => superAdminRequest('/api/super-admin/diagnostics/quickbooks'),
    enabled: diagnosticsOpen && isAuthenticated === true
  });

  const handleTestTokenRefresh = async (orgId: number) => {
    setTestingRefreshOrgId(orgId);
    setTestRefreshResult(null);

    try {
      const response = await fetch(
        `/api/super-admin/diagnostics/quickbooks/test-refresh/${orgId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      const result = await response.json();
      
      setTestRefreshResult({
        orgId,
        success: result.success || false,
        message: result.message || "Unknown result",
        logs: result.logs || []
      });
      
      if (result.success) {
        toast({
          title: "Token Refresh Test Passed",
          description: "The token was successfully refreshed",
        });
        refetchDiagnostics();
        refetchIntegrationStatus(); // Also refresh the integration status shown in the tab
      } else {
        toast({
          title: "Token Refresh Test Failed",
          description: result.message || "Test failed",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      setTestRefreshResult({
        orgId,
        success: false,
        message: error.message || "Network error",
        logs: []
      });
      toast({
        title: "Token Refresh Test Failed",
        description: error.message || "Network error",
        variant: "destructive"
      });
    } finally {
      setTestingRefreshOrgId(null);
    }
  };

  const createForm = useForm<OrganizationForm>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      primaryColor: "#2d5016",
      secondaryColor: "#8b7355",
      accentColor: "#EA590C",
      isActive: true,
      discoverable: true,
      kioskEnabled: false
    }
  });

  // Auto-generate slug from company name
  const watchedName = createForm.watch("name");
  useEffect(() => {
    if (watchedName && createDialogOpen) {
      const generatedSlug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      createForm.setValue("slug", generatedSlug);
    }
  }, [watchedName, createDialogOpen, createForm]);

  const editForm = useForm<OrganizationForm>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      primaryColor: "#2d5016",
      secondaryColor: "#8b7355",
      accentColor: "#EA590C",
      isActive: true,
      discoverable: true,
      kioskEnabled: false
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: OrganizationForm) => {
      return await superAdminRequest('/api/super-admin/organizations', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      setCreateDialogOpen(false);
      createForm.reset();
      toast({ title: "Organization created", description: "New client has been added successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create organization", variant: "destructive" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (data: OrganizationForm) => {
      if (!selectedOrg) return;
      return await superAdminRequest(`/api/super-admin/organizations/${selectedOrg.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      toast({ title: "Organization updated", description: "Changes saved successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update organization", variant: "destructive" });
    }
  });

  const impersonateMutation = useMutation({
    mutationFn: async (orgId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/impersonate`, {
        method: 'POST'
      });
    },
    onSuccess: (data: any) => {
      // Server set both super_admin_session (already present) and admin_session
      // httpOnly cookies. The two-cookie pair is the source of truth for
      // impersonation state — no client-side token storage needed.
      // currentOrgSlug for the impersonated org is read from the URL by
      // OrganizationContext / admin-route on the next page load.
      toast({
        title: "Entering Support Mode",
        description: `Accessing ${data.organization.name} dashboard...`
      });

      window.location.href = `/org/${data.organization.slug}/admin`;
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to impersonate organization", 
        variant: "destructive" 
      });
    }
  });

  const archiveMutation = useMutation({
    mutationFn: async (orgId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/archive`, {
        method: 'POST'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      setSelectedOrg(null);
      toast({ title: "Organization archived", description: "The organization has been archived and can be restored later." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to archive organization", variant: "destructive" });
    }
  });

  const restoreMutation = useMutation({
    mutationFn: async (orgId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/restore`, {
        method: 'POST'
      });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      if (data.organization) {
        setSelectedOrg(data.organization);
      }
      toast({ title: "Organization restored", description: "The organization has been restored and is now active." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to restore organization", variant: "destructive" });
    }
  });

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    editForm.reset({
      name: org.name,
      slug: org.slug,
      description: org.description || "",
      logoUrl: org.logoUrl || "",
      faviconUrl: org.faviconUrl || "",
      primaryColor: org.primaryColor || "#2d5016",
      secondaryColor: org.secondaryColor || "#8b7355",
      accentColor: org.accentColor || "#EA590C",
      companyEmail: org.companyEmail || "",
      companyPhone: org.companyPhone || "",
      companyAddress: org.companyAddress || "",
      twilioPhoneNumber: (org as any).twilioPhoneNumber || "",
      isActive: org.isActive,
      discoverable: org.discoverable ?? true,
      kioskEnabled: org.kioskEnabled ?? false
    });
    setActiveTab("details");
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Super Admin Portal</CardTitle>
            <CardDescription>Enter your password to access the admin portal</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter super admin password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10"
                    data-testid="input-super-admin-password"
                  />
                </div>
              </div>
              {loginError && (
                <Alert variant="destructive">
                  <AlertDescription>{loginError}</AlertDescription>
                </Alert>
              )}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoggingIn || !loginPassword}
                data-testid="btn-super-admin-login"
              >
                {isLoggingIn ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            Super Admin Portal
          </h1>
          <p className="text-gray-500 mt-1">Manage client organizations, branding, and integrations</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-create-org">
                <Plus className="h-4 w-4 mr-2" />
                New Client
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Client</DialogTitle>
              <DialogDescription>Add a new organization to the platform</DialogDescription>
            </DialogHeader>
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data), (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Fresh Produce Co" {...field} data-testid="input-org-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL Slug</FormLabel>
                      <FormControl>
                        <Input placeholder="fresh-produce-co" {...field} data-testid="input-org-slug" />
                      </FormControl>
                      <FormDescription>This will be used in the URL: /{field.value || "slug"}/</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="btn-submit-create-org">
                    {createMutation.isPending ? "Creating..." : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
          <Dialog open={diagnosticsOpen} onOpenChange={(open) => {
            setDiagnosticsOpen(open);
            // Refetch integration status when closing diagnostics (in case status changed)
            if (!open) {
              refetchIntegrationStatus();
            }
          }}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <SiQuickbooks className="h-5 w-5 text-green-600" />
                  QuickBooks Integration Diagnostics
                </DialogTitle>
                <DialogDescription>
                  View QuickBooks connection status across all organizations
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-500">
                  {qbDiagnostics && (
                    <>
                      Last checked: {new Date(qbDiagnostics.timestamp).toLocaleString()}
                      <span className="mx-2">•</span>
                      {qbDiagnostics.connectedOrgs} / {qbDiagnostics.totalOrgs} connected
                    </>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => refetchDiagnostics()}
                  disabled={isFetchingDiagnostics}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isFetchingDiagnostics ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              {isFetchingDiagnostics && !qbDiagnostics ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : qbDiagnostics?.diagnostics ? (
                <div className="space-y-3">
                  {qbDiagnostics.diagnostics.map((org) => (
                    <div 
                      key={org.orgId}
                      className={`p-4 rounded-lg border ${
                        org.quickbooks.isAuthenticated 
                          ? 'bg-green-50 border-green-200' 
                          : org.quickbooks.hasRecord 
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{org.orgName}</span>
                          <Badge variant="outline" className="text-xs">/{org.slug}</Badge>
                        </div>
                        {org.quickbooks.isAuthenticated ? (
                          <Badge className="bg-green-600">Connected</Badge>
                        ) : org.quickbooks.hasRecord ? (
                          <Badge variant="outline" className="text-yellow-700 border-yellow-400">Token Expired</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">Not Connected</Badge>
                        )}
                      </div>
                      
                      {org.quickbooks.hasRecord && (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600 mt-2">
                            <div>
                              <span className="text-gray-400">Company ID:</span>{' '}
                              {org.quickbooks.companyId || 'N/A'}
                            </div>
                            <div>
                              <span className="text-gray-400">Access Token:</span>{' '}
                              {org.quickbooks.hasAccessToken ? '✓' : '✗'}
                            </div>
                            <div>
                              <span className="text-gray-400">Refresh Token:</span>{' '}
                              {org.quickbooks.hasRefreshToken ? '✓' : '✗'}
                            </div>
                            <div>
                              <span className="text-gray-400">Expires:</span>{' '}
                              {org.quickbooks.expiresAt 
                                ? new Date(org.quickbooks.expiresAt).toLocaleString()
                                : 'N/A'}
                            </div>
                          </div>
                          
                          {org.quickbooks.hasRefreshToken && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTestTokenRefresh(org.orgId)}
                                disabled={testingRefreshOrgId === org.orgId}
                                className="text-xs"
                              >
                                {testingRefreshOrgId === org.orgId ? (
                                  <>
                                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                    Testing...
                                  </>
                                ) : (
                                  <>
                                    <TestTube className="h-3 w-3 mr-1" />
                                    Test Token Refresh
                                  </>
                                )}
                              </Button>
                              
                              {testRefreshResult?.orgId === org.orgId && (
                                <div className={`mt-2 p-2 rounded text-xs ${
                                  testRefreshResult.success 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  <p className="font-medium">{testRefreshResult.message}</p>
                                  {testRefreshResult.logs.length > 0 && (
                                    <details className="mt-1">
                                      <summary className="cursor-pointer text-xs opacity-75">View logs ({testRefreshResult.logs.length} entries)</summary>
                                      <pre className="mt-1 text-[10px] bg-black/10 p-1 rounded overflow-x-auto whitespace-pre-wrap">
                                        {testRefreshResult.logs.join('\n')}
                                      </pre>
                                    </details>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No diagnostic data available
                </div>
              )}
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => setLocation("/super-admin/onboarding")}
            data-testid="btn-super-admin-onboarding"
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            Onboarding
          </Button>
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="btn-super-admin-logout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Dashboard Overview Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-testid="stat-total-orgs">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-900">{dashboardStats.organizations.total}</p>
                  <p className="text-xs text-blue-700">Organizations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200" data-testid="stat-active-orgs">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">{dashboardStats.organizations.active}</p>
                  <p className="text-xs text-green-700">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200" data-testid="stat-total-orders">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{dashboardStats.totals.orders.toLocaleString()}</p>
                  <p className="text-xs text-purple-700">Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200" data-testid="stat-total-accounts">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-900">{dashboardStats.totals.accounts.toLocaleString()}</p>
                  <p className="text-xs text-amber-700">Accounts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200" data-testid="stat-total-products">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500 rounded-lg">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-cyan-900">{dashboardStats.totals.products.toLocaleString()}</p>
                  <p className="text-xs text-cyan-700">Products</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200" data-testid="stat-integrations">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-500 rounded-lg">
                  <Plug className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-900">
                    {dashboardStats.integrations.twilio + dashboardStats.integrations.gmail + dashboardStats.integrations.quickbooks}
                  </p>
                  <p className="text-xs text-rose-700">Integrations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Organizations</CardTitle>
                  <CardDescription>
                    {organizations.filter(o => !o.archivedAt).length} active
                    {organizations.filter(o => o.archivedAt).length > 0 && (
                      <span className="text-orange-600"> • {organizations.filter(o => o.archivedAt).length} archived</span>
                    )}
                  </CardDescription>
                </div>
                {organizations.filter(o => o.archivedAt).length > 0 && (
                  <Button
                    variant={showArchived ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowArchived(!showArchived)}
                    className="text-xs"
                  >
                    <Archive className="h-3 w-3 mr-1" />
                    {showArchived ? "Hide Archived" : "Show Archived"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                {isLoading ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : organizations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No organizations yet</div>
                ) : (
                  <div className="divide-y">
                    {organizations
                      .filter(org => showArchived || !org.archivedAt)
                      .map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleSelectOrg(org)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          selectedOrg?.id === org.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        } ${org.archivedAt ? 'opacity-60' : ''}`}
                        data-testid={`btn-select-org-${org.id}`}
                      >
                        <div>
                          <div className="font-medium text-gray-900">{org.name}</div>
                          <div className="text-sm text-gray-500">/{org.slug}/</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {org.archivedAt ? (
                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                              Archived
                            </Badge>
                          ) : (
                            <Badge variant={org.isActive ? "default" : "secondary"} className="text-xs">
                              {org.isActive ? "Active" : "Inactive"}
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <div className="col-span-8">
          {selectedOrg ? (
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedOrg.name}</CardTitle>
                    <CardDescription>/{selectedOrg.slug}/</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    {!selectedOrg.archivedAt && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => impersonateMutation.mutate(selectedOrg.id)}
                        disabled={impersonateMutation.isPending}
                        data-testid="btn-view-as-org"
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {impersonateMutation.isPending ? "Opening..." : "View as Org"}
                      </Button>
                    )}
                    {selectedOrg.archivedAt ? (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => restoreMutation.mutate(selectedOrg.id)}
                        disabled={restoreMutation.isPending}
                        data-testid="btn-restore-org"
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <RotateCcw className="h-4 w-4" />
                        {restoreMutation.isPending ? "Restoring..." : "Restore Organization"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Are you sure you want to archive "${selectedOrg.name}"? You can restore it later.`)) {
                            archiveMutation.mutate(selectedOrg.id);
                          }
                        }}
                        disabled={archiveMutation.isPending}
                        data-testid="btn-archive-org"
                        className="gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                      >
                        <Archive className="h-4 w-4" />
                        {archiveMutation.isPending ? "Archiving..." : "Archive"}
                      </Button>
                    )}
                    <div className="flex gap-2">
                      {integrationStatus && !selectedOrg.archivedAt && (
                        <>
                          <Badge variant={integrationStatus.twilio.configured ? "default" : "outline"} className="gap-1">
                            <Phone className="h-3 w-3" />
                            SMS
                          </Badge>
                          <Badge variant={integrationStatus.gmail.configured ? "default" : "outline"} className="gap-1">
                            <Mail className="h-3 w-3" />
                            Email
                          </Badge>
                          <Badge variant={integrationStatus.quickbooks.configured ? "default" : "outline"} className="gap-1">
                            <SiQuickbooks className="h-3 w-3" />
                            QB
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full flex-wrap justify-start rounded-none border-b h-auto p-0">
                    <TabsTrigger value="details" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <Settings className="h-4 w-4 mr-2" />
                      Details
                    </TabsTrigger>
                    <TabsTrigger value="branding" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <Palette className="h-4 w-4 mr-2" />
                      Branding
                    </TabsTrigger>
                    <TabsTrigger value="integrations" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <Plug className="h-4 w-4 mr-2" />
                      Integrations
                    </TabsTrigger>
                    <TabsTrigger value="products" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <Package className="h-4 w-4 mr-2" />
                      Products
                    </TabsTrigger>
                    <TabsTrigger value="customers" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Customers
                    </TabsTrigger>
                    <TabsTrigger value="team" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500">
                      <Users className="h-4 w-4 mr-2" />
                      Team
                    </TabsTrigger>
                    <TabsTrigger value="clyde" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500" data-testid="tab-clyde">
                      <CowboyHatIcon className="h-4 w-4 mr-2" />
                      Clyde
                    </TabsTrigger>
                  </TabsList>

                  <div className="p-6">
                    <TabsContent value="details" className="mt-0">
                      <OrganizationDetailsTab 
                        form={editForm} 
                        onSubmit={(data) => updateMutation.mutate(data)}
                        isPending={updateMutation.isPending}
                      />
                    </TabsContent>

                    <TabsContent value="branding" className="mt-0">
                      <BrandingTab 
                        orgId={selectedOrg.id}
                        form={editForm}
                        onSubmit={(data) => updateMutation.mutate(data)}
                        isPending={updateMutation.isPending}
                      />
                    </TabsContent>

                    <TabsContent value="integrations" className="mt-0">
                      <IntegrationsTab 
                        orgId={selectedOrg.id}
                        integrationStatus={integrationStatus}
                        twilioPhoneNumber={selectedOrg.twilioPhoneNumber || undefined}
                        onOpenDiagnostics={() => setDiagnosticsOpen(true)}
                      />
                    </TabsContent>

                    <TabsContent value="products" className="mt-0">
                      <ProductsTab orgId={selectedOrg.id} />
                    </TabsContent>

                    <TabsContent value="customers" className="mt-0">
                      <CustomersTab orgId={selectedOrg.id} orgSlug={selectedOrg.slug} />
                    </TabsContent>

                    <TabsContent value="team" className="mt-0">
                      <TeamTab orgId={selectedOrg.id} orgName={selectedOrg.name} />
                    </TabsContent>

                    <TabsContent value="clyde" className="mt-0">
                      <SuperAdminClydeSettings orgId={selectedOrg.id} orgName={selectedOrg.name} request={superAdminRequest} />
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-[600px] flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select an organization to manage</p>
                <p className="text-sm mt-1">Or create a new one to get started</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function OrganizationDetailsTab({ 
  form, 
  onSubmit, 
  isPending 
}: { 
  form: ReturnType<typeof useForm<OrganizationForm>>; 
  onSubmit: (data: OrganizationForm) => void;
  isPending: boolean;
}) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="input-edit-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL Slug</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="input-edit-slug" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Brief company description" data-testid="input-edit-description" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Separator />

        <div className="space-y-4">
          <h3 className="font-medium">Contact Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="companyEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} placeholder="contact@company.com" data-testid="input-edit-email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="companyPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="(555) 123-4567" data-testid="input-edit-phone" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="companyAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="123 Main St, City, State, ZIP" data-testid="input-edit-address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <FormLabel className="text-base">Active Status</FormLabel>
                <FormDescription>When disabled, customers cannot place orders</FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-active" />
              </FormControl>
            </FormItem>
          )}
        />

        {/* S10 per-org opt-out. The tenant admin Settings page has no org-profile
            editor (company email/phone live only here), so the toggle lives with
            them in the super-admin org editor. */}
        <FormField
          control={form.control}
          name="discoverable"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <FormLabel className="text-base">Discoverable in the FreshOrder app</FormLabel>
                <FormDescription>
                  Buyers can find this vendor by name on the app's launch screen. Off keeps the
                  vendor reachable only by direct link or code.
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-discoverable" />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Kiosk mode is a paid add-on: only FreshOrder switches it on. Tenant admins
            have no control for it — while it is off they do not see kiosk at all. */}
        <FormField
          control={form.control}
          name="kioskEnabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <FormLabel className="text-base">Kiosk mode (paid add-on)</FormLabel>
                <FormDescription>
                  On shows the vendor the Kiosk Mode tab in Settings and lets a kiosk be unlocked.
                  Off hides it and refuses a new kiosk session; the vendor's kiosk PIN is kept.
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-kiosk-enabled" />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isPending} data-testid="btn-save-details">
            <Save className="h-4 w-4 mr-2" />
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function BrandingTab({ 
  orgId,
  form, 
  onSubmit, 
  isPending 
}: { 
  orgId: number;
  form: ReturnType<typeof useForm<OrganizationForm>>; 
  onSubmit: (data: OrganizationForm) => void;
  isPending: boolean;
}) {
  const { toast } = useToast();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon') => {
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`/api/super-admin/organizations/${orgId}/upload/${type}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }
      
      const result = await response.json();
      
      if (type === 'logo') {
        form.setValue('logoUrl', result.url);
      } else {
        form.setValue('faviconUrl', result.url);
      }
      
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      toast({ title: `${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully` });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAsset = async (type: 'logo' | 'favicon') => {
    try {
      const response = await fetch(`/api/super-admin/organizations/${orgId}/upload/${type}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Delete failed');
      }
      
      if (type === 'logo') {
        form.setValue('logoUrl', '');
      } else {
        form.setValue('faviconUrl', '');
      }
      
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      toast({ title: `${type === 'logo' ? 'Logo' : 'Favicon'} removed` });
    } catch (error: any) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-6">
        <div className="space-y-4">
          <h3 className="font-medium">Logo & Icons</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label>Company Logo</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {form.watch("logoUrl") ? (
                  <div className="space-y-3">
                    <img 
                      src={form.watch("logoUrl")} 
                      alt="Logo preview" 
                      className="max-h-24 mx-auto object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                    />
                    <div className="flex gap-2 justify-center">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        disabled={uploadingLogo}
                        data-testid="btn-change-logo"
                      >
                        {uploadingLogo ? "Uploading..." : "Change"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteAsset('logo')}
                        data-testid="btn-delete-logo"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-2">Upload company logo</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                      disabled={uploadingLogo}
                      data-testid="btn-upload-logo"
                    >
                      {uploadingLogo ? "Uploading..." : "Choose File"}
                    </Button>
                  </div>
                )}
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'logo');
                    e.target.value = '';
                  }}
                  data-testid="input-logo-file"
                />
              </div>
              <p className="text-xs text-gray-500">PNG, JPG, GIF, or SVG. Max 5MB.</p>
            </div>

            <div className="space-y-3">
              <Label>Favicon / Default Product Image</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {form.watch("faviconUrl") ? (
                  <div className="space-y-3">
                    <img 
                      src={form.watch("faviconUrl")} 
                      alt="Favicon preview" 
                      className="h-12 w-12 mx-auto object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                    />
                    <div className="flex gap-2 justify-center">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => document.getElementById('favicon-upload')?.click()}
                        disabled={uploadingFavicon}
                        data-testid="btn-change-favicon"
                      >
                        {uploadingFavicon ? "Uploading..." : "Change"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteAsset('favicon')}
                        data-testid="btn-delete-favicon"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-2">Upload square image</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('favicon-upload')?.click()}
                      disabled={uploadingFavicon}
                      data-testid="btn-upload-favicon"
                    >
                      {uploadingFavicon ? "Uploading..." : "Choose File"}
                    </Button>
                  </div>
                )}
                <input
                  id="favicon-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'favicon');
                    e.target.value = '';
                  }}
                  data-testid="input-favicon-file"
                />
              </div>
              <p className="text-xs text-gray-500">Square image (PNG, JPG, ICO, or SVG). Used for browser tab icon and default product images.</p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="font-medium">Brand Colors</h3>
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="primaryColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary Color</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input type="color" {...field} className="w-12 h-10 p-1 cursor-pointer" data-testid="input-primary-color" />
                    </FormControl>
                    <Input value={field.value} onChange={field.onChange} className="flex-1 font-mono text-sm" />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secondaryColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Secondary Color</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input type="color" {...field} className="w-12 h-10 p-1 cursor-pointer" data-testid="input-secondary-color" />
                    </FormControl>
                    <Input value={field.value} onChange={field.onChange} className="flex-1 font-mono text-sm" />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accentColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Accent Color</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input type="color" {...field} className="w-12 h-10 p-1 cursor-pointer" data-testid="input-accent-color" />
                    </FormControl>
                    <Input value={field.value} onChange={field.onChange} className="flex-1 font-mono text-sm" />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-3">Color Preview:</p>
            <div className="flex gap-2">
              <div 
                className="w-16 h-16 rounded-lg shadow-sm border"
                style={{ backgroundColor: form.watch("primaryColor") || "#2d5016" }}
              />
              <div 
                className="w-16 h-16 rounded-lg shadow-sm border"
                style={{ backgroundColor: form.watch("secondaryColor") || "#8b7355" }}
              />
              <div 
                className="w-16 h-16 rounded-lg shadow-sm border"
                style={{ backgroundColor: form.watch("accentColor") || "#EA590C" }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={isPending} data-testid="btn-save-branding">
            <Save className="h-4 w-4 mr-2" />
            {isPending ? "Saving..." : "Save Colors"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function IntegrationsTab({ 
  orgId, 
  integrationStatus,
  twilioPhoneNumber,
  onOpenDiagnostics
}: { 
  orgId: number;
  integrationStatus?: IntegrationStatus;
  twilioPhoneNumber?: string;
  onOpenDiagnostics?: () => void;
}) {
  return (
    <div className="space-y-6">
      <TwilioIntegration orgId={orgId} status={integrationStatus?.twilio} currentPhoneNumber={twilioPhoneNumber} />
      <Separator />
      <GmailIntegration orgId={orgId} status={integrationStatus?.gmail} />
      <Separator />
      <QuickBooksIntegration orgId={orgId} status={integrationStatus?.quickbooks} onOpenDiagnostics={onOpenDiagnostics} />
    </div>
  );
}

function TwilioIntegration({ orgId, status, currentPhoneNumber }: { orgId: number; status?: { configured: boolean }; currentPhoneNumber?: string }) {
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber || "");
  const [testPhone, setTestPhone] = useState("");

  useEffect(() => {
    setPhoneNumber(currentPhoneNumber || "");
  }, [currentPhoneNumber]);

  const isConfigured = !!phoneNumber;

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify({ twilioPhoneNumber: phoneNumber })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      toast({ title: "Phone number saved" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/twilio/test`, {
        method: 'POST',
        body: JSON.stringify({ testPhone })
      });
    },
    onSuccess: (data: any) => {
      toast({ title: data.success ? "Test SMS sent!" : "Test failed", description: data.message || data.error });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify({ twilioPhoneNumber: "" })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations'] });
      setPhoneNumber("");
      toast({ title: "Phone number removed" });
    }
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Phone className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-medium">Twilio SMS</h3>
            <p className="text-sm text-gray-500">Send SMS reminders and notifications</p>
          </div>
        </div>
        <Badge variant={isConfigured ? "default" : "secondary"}>
          {isConfigured ? "Configured" : "Not Configured"}
        </Badge>
      </div>

      <Alert>
        <AlertDescription className="text-sm">
          SMS is sent using FreshOrder's master Twilio account. Set the phone number this organization will send from.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Organization's Twilio Phone Number</Label>
          <Input 
            value={phoneNumber} 
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            data-testid="input-twilio-phone"
          />
          <p className="text-xs text-gray-500 mt-1">Must be a number in FreshOrder's Twilio account</p>
        </div>
        <div>
          <Label>Test Phone Number</Label>
          <Input 
            value={testPhone} 
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="+1234567890"
            data-testid="input-twilio-test-phone"
          />
          <p className="text-xs text-gray-500 mt-1">Where to send the test message</p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button 
          onClick={() => saveMutation.mutate()} 
          disabled={saveMutation.isPending || !phoneNumber}
          data-testid="btn-save-twilio"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Phone Number
        </Button>
        {isConfigured && (
          <Button 
            variant="destructive" 
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isPending}
            data-testid="btn-clear-twilio"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear
          </Button>
        )}
        <Button 
          variant="outline" 
          onClick={() => testMutation.mutate()}
          disabled={testMutation.isPending || !isConfigured || !testPhone}
          data-testid="btn-test-twilio"
        >
          <TestTube className="h-4 w-4 mr-2" />
          Send Test SMS
        </Button>
      </div>
    </div>
  );
}

function GmailIntegration({ orgId, status }: { orgId: number; status?: { configured: boolean; connected?: boolean; fromEmail?: string } }) {
  const { toast } = useToast();
  const [testEmail, setTestEmail] = useState("");
  
  const isConnected = status?.connected ?? status?.configured ?? false;

  const testMutation = useMutation({
    mutationFn: async () => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/gmail/test`, {
        method: 'POST',
        body: JSON.stringify({ testEmail })
      });
    },
    onSuccess: (data: any) => {
      toast({ title: data.success ? "Test email sent!" : "Test failed", description: data.error || undefined });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium">Gmail Email</h3>
            <p className="text-sm text-gray-500">Send email notifications and order confirmations</p>
          </div>
        </div>
        <Badge variant={isConnected ? "default" : "destructive"}>
          {isConnected ? "Connected" : "Not Connected"}
        </Badge>
      </div>

      <Alert>
        <AlertDescription className="text-sm">
          Email is sent using FreshOrder's centralized Gmail account (noreply.freshorder@gmail.com). 
          The organization's name is displayed as the sender name.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-4 items-end">
        <div>
          <Label>Test Email Address</Label>
          <Input 
            type="email"
            value={testEmail} 
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            data-testid="input-gmail-test-email"
          />
        </div>
        <div>
          <Button 
            variant="outline" 
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending || !testEmail}
            data-testid="btn-test-gmail"
          >
            <TestTube className="h-4 w-4 mr-2" />
            Send Test Email
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuickBooksIntegration({ orgId, status, onOpenDiagnostics }: { orgId: number; status?: { configured: boolean; realmId?: string }; onOpenDiagnostics?: () => void }) {
  const { toast } = useToast();
  const [isPolling, setIsPolling] = useState(false);

  const connectMutation = useMutation({
    mutationFn: async () => {
      const res = await superAdminRequest(`/api/super-admin/organizations/${orgId}/quickbooks/auth-url`);
      return res;
    },
    onSuccess: (data: any) => {
      window.open(data.authUrl, '_blank');
      // Start polling for connection status after opening OAuth window
      setIsPolling(true);
      let attempts = 0;
      const maxAttempts = 60; // Poll for up to 2 minutes (every 2 seconds)
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          // Invalidate and refetch the integrations query
          await queryClient.invalidateQueries({ 
            queryKey: ['/api/super-admin/organizations', orgId, 'integrations'] 
          });
          // Check if now connected
          const updatedStatus = queryClient.getQueryData<IntegrationStatus>(
            ['/api/super-admin/organizations', orgId, 'integrations']
          );
          if (updatedStatus?.quickbooks?.configured) {
            clearInterval(pollInterval);
            setIsPolling(false);
            toast({ title: "QuickBooks connected!", description: "OAuth completed successfully" });
          }
        } catch (error) {
          // Silently continue polling
        }
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setIsPolling(false);
        }
      }, 2000);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/quickbooks`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'integrations'] });
      toast({ title: "QuickBooks disconnected" });
    }
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <SiQuickbooks className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-medium">QuickBooks Online</h3>
            <p className="text-sm text-gray-500">Sync invoices and customer data</p>
            {status?.realmId && (
              <p className="text-xs text-gray-400">Company ID: {status.realmId}</p>
            )}
          </div>
        </div>
        <Badge variant={status?.configured ? "default" : "secondary"}>
          {status?.configured ? "Connected" : "Not Connected"}
        </Badge>
      </div>

      <div className="flex gap-2">
        {status?.configured ? (
          <Button 
            variant="destructive" 
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            data-testid="btn-disconnect-qb"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Disconnect
          </Button>
        ) : (
          <Button 
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending || isPolling}
            data-testid="btn-connect-qb"
          >
            {isPolling ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Waiting for OAuth...
              </>
            ) : (
              <>
                <ExternalLink className="h-4 w-4 mr-2" />
                Connect to QuickBooks
              </>
            )}
          </Button>
        )}
        {onOpenDiagnostics && (
          <Button 
            variant="outline" 
            onClick={onOpenDiagnostics}
            data-testid="btn-qb-diagnostics"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Diagnostics
          </Button>
        )}
      </div>
    </div>
  );
}

function ProductsTab({ orgId }: { orgId: number }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkImageInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    categoriesCreated: number;
    errors: string[];
  } | null>(null);
  const [bulkImageModalOpen, setBulkImageModalOpen] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  // Set after a successful import so we can offer image matching as the
  // natural next step, rather than leaving a catalogue full of placeholders.
  const [offerImageMatch, setOfferImageMatch] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [bulkImagePreview, setBulkImagePreview] = useState<string | null>(null);
  const [bulkImageFile, setBulkImageFile] = useState<File | null>(null);

  const { data: products = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/super-admin/organizations', orgId, 'products'],
    queryFn: () => superAdminRequest(`/api/super-admin/organizations/${orgId}/products`)
  });

  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ['/api/super-admin/organizations', orgId, 'categories'],
    queryFn: () => superAdminRequest(`/api/super-admin/organizations/${orgId}/categories`)
  });

  const bulkImageMutation = useMutation({
    mutationFn: async ({ categoryId, file }: { categoryId: number; file: File }) => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('categoryId', categoryId.toString());
      
      const response = await fetch(`/api/super-admin/organizations/${orgId}/products/bulk-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.status === 401) {
        throw new Error("Session expired - please log in again");
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update product images');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'products'] });
      toast({
        title: "Images Updated",
        description: `Updated ${data.updatedCount} products with the new image`
      });
      setBulkImageModalOpen(false);
      setSelectedCategoryId("");
      setBulkImagePreview(null);
      setBulkImageFile(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleBulkImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setBulkImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBulkImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBulkImageSubmit = () => {
    if (!selectedCategoryId || !bulkImageFile) {
      toast({
        title: "Missing Information",
        description: "Please select a category and upload an image",
        variant: "destructive"
      });
      return;
    }
    bulkImageMutation.mutate({ categoryId: parseInt(selectedCategoryId), file: bulkImageFile });
  };

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`/api/super-admin/organizations/${orgId}/products/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.status === 401) {
        throw new Error("Session expired - please log in again");
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to import products');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setImportResult(data);
      setOfferImageMatch(data.imported > 0);
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'products'] });
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'categories'] });
      toast({
        title: "Import Complete",
        description: `Imported ${data.imported} products${data.categoriesCreated > 0 ? `, created ${data.categoriesCreated} new categories` : ''}${data.skipped > 0 ? `, skipped ${data.skipped} duplicates` : ''}`
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportResult(null);
      importMutation.mutate(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading products...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Product Catalog</h3>
          <p className="text-sm text-gray-500">{products.length} products in {categories.length} categories</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={importMutation.isPending}
          >
            {importMutation.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import Products
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setMatchDialogOpen(true)}
            disabled={products.length === 0}
            title="Propose images from the shared stock library for review"
          >
            <Image className="h-4 w-4 mr-2" />
            Match Images
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setBulkImageModalOpen(true)}
          >
            <Image className="h-4 w-4 mr-2" />
            Bulk Set Image
          </Button>
          <p className="text-xs text-gray-500 mt-1">Excel file with columns: name, category (required), subheading (optional)</p>
        </div>
      </div>

      <StockImageMatchDialog
        orgId={orgId}
        open={matchDialogOpen}
        onOpenChange={(o) => {
          setMatchDialogOpen(o);
          if (!o) setOfferImageMatch(false);
        }}
      />

      {/* Offered right after an import -- the products exist but have no
          imagery yet, which is exactly when matching is worth running. */}
      {offerImageMatch && !matchDialogOpen && (
        <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
          <Image className="h-5 w-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-medium text-blue-900">Add images to these products?</div>
            <p className="text-blue-700 text-xs">
              Scan the imported catalogue against the shared stock library. You review and
              approve every match before anything is applied.
            </p>
          </div>
          <Button size="sm" onClick={() => setMatchDialogOpen(true)}>Scan &amp; match</Button>
          <Button size="sm" variant="ghost" onClick={() => setOfferImageMatch(false)}>Not now</Button>
        </div>
      )}

      {/* Bulk Image Modal */}
      <Dialog open={bulkImageModalOpen} onOpenChange={setBulkImageModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Set Product Images</DialogTitle>
            <DialogDescription>
              Set the same image for all products in a category
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat: any) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name} ({products.filter((p: any) => p.categoryId === cat.id).length} products)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <input
                type="file"
                ref={bulkImageInputRef}
                onChange={handleBulkImageSelect}
                accept="image/*"
                className="hidden"
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => bulkImageInputRef.current?.click()}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {bulkImageFile ? bulkImageFile.name : 'Choose Image'}
                </Button>
              </div>
              {bulkImagePreview && (
                <div className="mt-2">
                  <img src={bulkImagePreview} alt="Preview" className="w-24 h-24 object-cover rounded" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBulkImageModalOpen(false);
              setSelectedCategoryId("");
              setBulkImagePreview(null);
              setBulkImageFile(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkImageSubmit}
              disabled={!selectedCategoryId || !bulkImageFile || bulkImageMutation.isPending}
            >
              {bulkImageMutation.isPending ? 'Updating...' : 'Apply to All Products'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {importResult && importResult.errors.length > 0 && (
        <Alert>
          <AlertDescription>
            <div className="text-sm">
              <p className="font-medium mb-1">Import Notes:</p>
              <ul className="list-disc pl-4 space-y-0.5 text-gray-600">
                {importResult.errors.slice(0, 5).map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
                {importResult.errors.length > 5 && (
                  <li>...and {importResult.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No products yet</p>
          <p className="text-sm text-gray-400 mt-1">Products can be managed from the client's settings page</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Product</th>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Category</th>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.slice(0, 10).map((product: any) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {product.imagePath && (
                        <img src={product.imagePath} alt="" className="w-10 h-10 rounded object-cover" />
                      )}
                      <span className="font-medium">{product.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-gray-500">
                    {categories.find((c: any) => c.id === product.categoryId)?.name || 'Uncategorized'}
                  </td>
                  <td className="p-3">
                    <Badge variant={product.isActive ? "default" : "secondary"}>
                      {product.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length > 10 && (
            <div className="p-3 text-center text-sm text-gray-500 bg-gray-50 border-t">
              Showing 10 of {products.length} products
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CustomersTab({ orgId, orgSlug }: { orgId: number; orgSlug: string }) {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any | null>(null);

  const { data: accounts = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/super-admin/organizations', orgId, 'accounts'],
    queryFn: () => superAdminRequest(`/api/super-admin/organizations/${orgId}/accounts`)
  });

  const accountForm = useForm({
    defaultValues: {
      name: "",
      phone: "",
      customUrl: "",
      isActive: true
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/accounts`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/stats'] });
      setCreateDialogOpen(false);
      accountForm.reset();
      toast({ title: "Account created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ accountId, data }: { accountId: number; data: any }) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/accounts/${accountId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'accounts'] });
      setEditingAccount(null);
      accountForm.reset();
      toast({ title: "Account updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (accountId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/accounts/${accountId}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/stats'] });
      toast({ title: "Account deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const handleCreateSubmit = (data: any) => {
    createMutation.mutate(data);
  };

  const handleEditSubmit = (data: any) => {
    if (editingAccount) {
      updateMutation.mutate({ accountId: editingAccount.id, data });
    }
  };

  const handleEdit = (account: any) => {
    setEditingAccount(account);
    accountForm.reset({
      name: account.name || "",
      phone: account.phone || "",
      customUrl: account.customUrl || "",
      isActive: account.isActive ?? true
    });
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading accounts...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Customer Accounts</h3>
          <p className="text-sm text-gray-500">{accounts.length} accounts</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-create-account">
              <Plus className="h-4 w-4 mr-2" />
              New Account
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Account</DialogTitle>
              <DialogDescription>Add a new customer account for this organization</DialogDescription>
            </DialogHeader>
            <form onSubmit={accountForm.handleSubmit(handleCreateSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Account Name</Label>
                <Input 
                  id="name" 
                  {...accountForm.register("name", { required: true })} 
                  placeholder="Restaurant Name"
                  data-testid="input-account-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input 
                  id="phone" 
                  {...accountForm.register("phone")} 
                  placeholder="555-123-4567"
                  data-testid="input-account-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customUrl">Custom URL Slug</Label>
                <Input 
                  id="customUrl" 
                  {...accountForm.register("customUrl")} 
                  placeholder="restaurant-name"
                  data-testid="input-account-url"
                />
                <p className="text-xs text-gray-500">
                  Order page: /org/{orgSlug}/order/{accountForm.watch("customUrl") || "slug"}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending} data-testid="btn-submit-account">
                  {createMutation.isPending ? "Creating..." : "Create Account"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {accounts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No customer accounts yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first account to start accepting orders</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Account Name</th>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Phone</th>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Order URL</th>
                <th className="text-left p-3 text-sm font-medium text-gray-500">Status</th>
                <th className="text-right p-3 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {accounts.map((account: any) => (
                <tr key={account.id} className="hover:bg-gray-50" data-testid={`row-account-${account.id}`}>
                  <td className="p-3 font-medium">{account.name}</td>
                  <td className="p-3 text-gray-500">{account.phone || "-"}</td>
                  <td className="p-3">
                    {account.customUrl ? (
                      <a 
                        href={`/org/${orgSlug}/order/${account.customUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        /order/{account.customUrl}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge variant={account.isActive ? "default" : "secondary"}>
                      {account.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Dialog open={editingAccount?.id === account.id} onOpenChange={(open) => !open && setEditingAccount(null)}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(account)}
                            data-testid={`btn-edit-account-${account.id}`}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit Account</DialogTitle>
                            <DialogDescription>Update account information</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={accountForm.handleSubmit(handleEditSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit-name">Account Name</Label>
                              <Input 
                                id="edit-name" 
                                {...accountForm.register("name", { required: true })} 
                                data-testid="input-edit-account-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit-phone">Phone Number</Label>
                              <Input 
                                id="edit-phone" 
                                {...accountForm.register("phone")} 
                                data-testid="input-edit-account-phone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit-customUrl">Custom URL Slug</Label>
                              <Input 
                                id="edit-customUrl" 
                                {...accountForm.register("customUrl")} 
                                data-testid="input-edit-account-url"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch 
                                checked={accountForm.watch("isActive")} 
                                onCheckedChange={(checked) => accountForm.setValue("isActive", checked)}
                                data-testid="switch-account-active"
                              />
                              <Label>Active</Label>
                            </div>
                            <DialogFooter>
                              <Button type="button" variant="outline" onClick={() => setEditingAccount(null)}>Cancel</Button>
                              <Button type="submit" disabled={updateMutation.isPending} data-testid="btn-save-account">
                                {updateMutation.isPending ? "Saving..." : "Save Changes"}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          if (confirm(`Delete account "${account.name}"? This cannot be undone.`)) {
                            deleteMutation.mutate(account.id);
                          }
                        }}
                        data-testid={`btn-delete-account-${account.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeamTab({ orgId, orgName }: { orgId: number; orgName: string }) {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<any | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { data: users = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/super-admin/organizations', orgId, 'users'],
    queryFn: () => superAdminRequest(`/api/super-admin/organizations/${orgId}/users`)
  });

  const userForm = useForm({
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: "admin",
      isActive: true
    }
  });

  const resetPasswordForm = useForm({
    defaultValues: {
      newPassword: ""
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'users'] });
      setCreateDialogOpen(false);
      userForm.reset();
      toast({ title: "User created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: number; data: any }) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'users'] });
      setEditingUser(null);
      userForm.reset();
      toast({ title: "User updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: number; newPassword: string }) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users/${userId}/reset-password`, {
        method: 'PUT',
        body: JSON.stringify({ newPassword })
      });
    },
    onSuccess: () => {
      setResetPasswordUser(null);
      resetPasswordForm.reset();
      toast({ title: "Password reset successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users/${userId}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'users'] });
      toast({ title: "User deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const inviteMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users/${userId}/invite`, {
        method: 'POST'
      });
    },
    onSuccess: async (data: any) => {
      // Email delivery is best-effort; the invite token is always created. When
      // the send fails the server returns emailSent:false + a working link so we
      // can hand the operator a usable fallback instead of a dead-end error.
      if (data?.emailSent === false && data?.inviteLink) {
        try {
          await navigator.clipboard.writeText(data.inviteLink);
          toast({
            title: "Email couldn't be sent — link copied",
            description: `Invite link copied to clipboard (expires in ${data.expiresIn}). Send it to the user directly.`,
            variant: "destructive",
          });
        } catch {
          prompt("Email couldn't be sent. Copy this invite link and send it to the user:", data.inviteLink);
        }
        return;
      }
      toast({ title: "Invitation sent", description: "Email sent successfully" });
    },
    onError: (error: any) => {
      console.error('[INVITE] Error:', error);
      toast({ title: "Failed to send invite", description: error.message, variant: "destructive" });
    }
  });

  const generateLinkMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await superAdminRequest(`/api/super-admin/organizations/${orgId}/users/${userId}/generate-link`, {
        method: 'POST'
      });
    },
    onSuccess: async (data: any) => {
      try {
        await navigator.clipboard.writeText(data.inviteLink);
        toast({ title: "Link copied!", description: `Expires in ${data.expiresIn}. Share this link with the user.` });
      } catch {
        prompt("Copy this invite link:", data.inviteLink);
      }
    },
    onError: (error: any) => {
      toast({ title: "Failed to generate link", description: error.message, variant: "destructive" });
    }
  });

  const handleCreateSubmit = (data: any) => {
    createMutation.mutate(data);
  };

  const handleEditSubmit = (data: any) => {
    if (editingUser) {
      updateMutation.mutate({ userId: editingUser.id, data: {
        username: data.username,
        role: data.role,
        isActive: data.isActive
      }});
    }
  };

  const handleEdit = (user: any) => {
    setEditingUser(user);
    userForm.reset({
      username: user.username || "",
      email: user.email || "",
      password: "",
      role: user.role || "admin",
      isActive: user.isActive ?? true
    });
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading team members...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Team Members</h3>
          <p className="text-sm text-gray-500">Admin and staff users who can access the {orgName} dashboard</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-create-user">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>Create a new admin or staff login for {orgName}</DialogDescription>
            </DialogHeader>
            <form onSubmit={userForm.handleSubmit(handleCreateSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input 
                  id="username" 
                  {...userForm.register("username", { required: true })} 
                  placeholder="johndoe"
                  data-testid="input-user-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (for login)</Label>
                <Input 
                  id="email" 
                  type="email"
                  {...userForm.register("email", { required: true })} 
                  placeholder="john@example.com"
                  data-testid="input-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"}
                    {...userForm.register("password", { required: true, minLength: 6 })} 
                    placeholder="Min 6 characters"
                    data-testid="input-user-password"
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select 
                  id="role"
                  {...userForm.register("role")}
                  className="w-full p-2 border rounded-md"
                  data-testid="select-user-role"
                >
                  <option value="admin">Admin (full access)</option>
                  <option value="staff">Staff (limited access)</option>
                </select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending} data-testid="btn-submit-user">
                  {createMutation.isPending ? "Creating..." : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No team members yet</p>
          <p className="text-sm text-gray-400 mt-1">Add users to allow them to access the admin dashboard</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-gray-600">Username</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">Role</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right p-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((user: any) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="p-3 font-medium">{user.username}</td>
                  <td className="p-3 text-gray-500">{user.email || '-'}</td>
                  <td className="p-3">
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role === 'admin' ? 'Admin' : 'Staff'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Badge variant={user.isActive ? "default" : "secondary"}>
                      {user.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Dialog open={editingUser?.id === user.id} onOpenChange={(open) => !open && setEditingUser(null)}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(user)}
                            data-testid={`btn-edit-user-${user.id}`}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit User</DialogTitle>
                            <DialogDescription>Update user information</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={userForm.handleSubmit(handleEditSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit-username">Username</Label>
                              <Input 
                                id="edit-username" 
                                {...userForm.register("username", { required: true })} 
                                data-testid="input-edit-user-username"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit-role">Role</Label>
                              <select 
                                id="edit-role"
                                {...userForm.register("role")}
                                className="w-full p-2 border rounded-md"
                                data-testid="select-edit-user-role"
                              >
                                <option value="admin">Admin (full access)</option>
                                <option value="staff">Staff (limited access)</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch 
                                checked={userForm.watch("isActive")} 
                                onCheckedChange={(checked) => userForm.setValue("isActive", checked)}
                                data-testid="switch-user-active"
                              />
                              <Label>Active</Label>
                            </div>
                            <DialogFooter>
                              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
                              <Button type="submit" disabled={updateMutation.isPending} data-testid="btn-save-user">
                                {updateMutation.isPending ? "Saving..." : "Save Changes"}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={resetPasswordUser?.id === user.id} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setResetPasswordUser(user)}
                            data-testid={`btn-reset-password-${user.id}`}
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Reset Password</DialogTitle>
                            <DialogDescription>Set a new password for {user.username}</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={resetPasswordForm.handleSubmit((data) => {
                            resetPasswordMutation.mutate({ userId: user.id, newPassword: data.newPassword });
                          }, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="new-password">New Password</Label>
                              <Input 
                                id="new-password" 
                                type="password"
                                {...resetPasswordForm.register("newPassword", { required: true, minLength: 6 })} 
                                placeholder="Min 6 characters"
                                data-testid="input-new-password"
                              />
                            </div>
                            <DialogFooter>
                              <Button type="button" variant="outline" onClick={() => setResetPasswordUser(null)}>Cancel</Button>
                              <Button type="submit" disabled={resetPasswordMutation.isPending} data-testid="btn-submit-password">
                                {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => inviteMutation.mutate(user.id)}
                        disabled={!user.email || inviteMutation.isPending}
                        title={user.email ? "Send invitation email" : "User needs an email address"}
                        data-testid={`btn-invite-user-${user.id}`}
                      >
                        <Mail className="h-4 w-4 text-blue-500" />
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => generateLinkMutation.mutate(user.id)}
                        disabled={generateLinkMutation.isPending}
                        title="Generate invite link (copies to clipboard)"
                        data-testid={`btn-copy-link-${user.id}`}
                      >
                        <Link2 className="h-4 w-4 text-green-500" />
                      </Button>

                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          if (confirm(`Delete user "${user.username}"? This cannot be undone.`)) {
                            deleteMutation.mutate(user.id);
                          }
                        }}
                        data-testid={`btn-delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
